import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Info as InfoIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { motion, useAnimation, useInView } from 'framer-motion';
import CountUp from 'react-countup';

interface MetricData {
  name: string;
  unit: string;
  initialValue: number;
  currentValue: number;
  targetValue?: number;
  isPositive: boolean; // true if higher is better
  description?: string;
  icon?: React.ReactNode;
  category?: string;
}

interface AnimatedMetricCardProps {
  metric: MetricData;
  delay?: number;
  onCardClick?: () => void;
  showTrend?: boolean;
  variant?: 'default' | 'hero' | 'compact';
}

export const AnimatedMetricCard: React.FC<AnimatedMetricCardProps> = ({
  metric,
  delay = 0,
  onCardClick,
  showTrend = true,
  variant = 'default',
}) => {
  const theme = useTheme();
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [isHovered, setIsHovered] = useState(false);

  // Calculate improvement
  const percentChange = metric.initialValue !== 0
    ? ((metric.currentValue - metric.initialValue) / Math.abs(metric.initialValue)) * 100
    : 0;
  
  const isImprovement = metric.isPositive 
    ? metric.currentValue > metric.initialValue
    : metric.currentValue < metric.initialValue;
  
  const changeColor = isImprovement 
    ? theme.palette.success.main 
    : percentChange === 0 
    ? theme.palette.warning.main 
    : theme.palette.error.main;

  // Get trend icon
  const getTrendIcon = () => {
    if (percentChange === 0) return <TrendingFlatIcon />;
    if (isImprovement) return <TrendingUpIcon />;
    return <TrendingDownIcon />;
  };

  // Animation variants
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        delay: Math.min(delay, 0.2),
      },
    },
    hover: {
      y: -4,
      transition: {
        duration: 0.2,
      },
    },
  };

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const getCardHeight = () => {
    switch (variant) {
      case 'hero': return 280;
      case 'compact': return 160;
      default: return 220;
    }
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      whileHover={isHovered ? 'hover' : undefined}
      variants={cardVariants}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      style={{ height: '100%' }}
    >
      <Paper
        elevation={isHovered ? 3 : 1}
        onClick={onCardClick}
        sx={{
          p: 3,
          height: getCardHeight(),
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          cursor: onCardClick ? 'pointer' : 'default',
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          transition: 'all 0.2s ease-out',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
        }}
      >

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography 
              variant={variant === 'hero' ? 'h6' : 'subtitle2'} 
              color="text.secondary" 
              gutterBottom
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {metric.icon}
              {metric.name}
            </Typography>
            {metric.category && (
              <Chip 
                label={metric.category} 
                size="small" 
                sx={{ 
                  height: 20,
                  fontSize: '0.7rem',
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                }}
              />
            )}
          </Box>
          
          {metric.description && (
            <Tooltip title={metric.description} placement="top">
              <IconButton size="small" sx={{ ml: 1 }}>
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Value Display */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: Math.min(delay, 0.2) + 0.1, duration: 0.2 }}
          >
            <Typography 
              variant={variant === 'hero' ? 'h2' : 'h3'} 
              component="div" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                display: 'flex',
                alignItems: 'baseline',
                gap: 1,
              }}
            >
              <CountUp
                start={0}
                end={metric.currentValue}
                duration={1.2}
                delay={0}
                decimals={metric.unit === 'kg' || metric.unit === '%' ? 1 : 0}
                separator=","
              />
              <Typography 
                component="span" 
                variant={variant === 'hero' ? 'h4' : 'h5'}
                color="text.secondary"
              >
                {metric.unit}
              </Typography>
            </Typography>
          </motion.div>

          {/* Target indicator */}
          {metric.targetValue !== undefined && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Target: {metric.targetValue} {metric.unit}
              </Typography>
              <Box 
                sx={{ 
                  width: '100%', 
                  height: 4, 
                  backgroundColor: alpha(theme.palette.divider, 0.2),
                  borderRadius: 2,
                  mt: 0.5,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{
                    width: `${Math.min((metric.currentValue / metric.targetValue) * 100, 100)}%`
                  }}
                  transition={{ delay: 0, duration: 0.6 }}
                  style={{
                    height: '100%',
                    backgroundColor: changeColor,
                    borderRadius: 2,
                  }}
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Trend Indicator */}
        {showTrend && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: Math.min(delay, 0.2) + 0.15, duration: 0.2 }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: changeColor,
                }}
              >
                {getTrendIcon()}
                <Typography variant="body2" fontWeight={600}>
                  {Math.abs(percentChange).toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                from initial
              </Typography>
              {onCardClick && (
                <Box sx={{ ml: 'auto' }}>
                  <IconButton size="small" color="primary">
                    <TimelineIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
          </motion.div>
        )}

        {/* Bottom accent */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 3,
            backgroundColor: changeColor,
            opacity: 0.2,
          }}
        />
      </Paper>
    </motion.div>
  );
};

export default AnimatedMetricCard;